import { API_BASE_URL as BASE_URL } from './apiBase';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// When the server tells us our auth is no good (401/403), wipe the local state and
// reload. Catches the "stale token / cookie from a previous deploy keeps the app
// silently broken" class of bug — the same situation that needed a manual cookie
// clear on desktop. JS-visible cookies are cleared too; httpOnly stays server-side.
let authExpiredHandled = false;
function handleAuthExpired(reason) {
  if (authExpiredHandled) return; // avoid reload loop on burst of failures
  authExpiredHandled = true;
  console.warn('🔒 Auth expired, clearing local state and reloading:', reason);
  try {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('activeGameId');
  } catch (_) {}
  if (typeof document !== 'undefined') {
    document.cookie.split(';').forEach(c => {
      const name = c.split('=')[0] && c.split('=')[0].trim();
      if (name) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    });
  }
  if (typeof window !== 'undefined') {
    setTimeout(() => window.location.reload(), 200);
  }
}

function checkAuthOk(response) {
  if (response.status === 401 || response.status === 403) {
    handleAuthExpired(`HTTP ${response.status} on ${response.url || '(unknown URL)'}`);
    const err = new Error('Your session has expired — please log in again.');
    err.kind = 'auth-expired';
    err.status = response.status;
    throw err;
  }
}

export const api = {
  // Authentication
  async signup(username, email, password, displayName) {
    const response = await fetch(`${BASE_URL}/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, email, password, displayName }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Signup failed');
    }
    
    const data = await response.json();
    // Store token in localStorage
    if (data.token) {
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  async login(username, password) {
    const response = await fetch(`${BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }
    
    const data = await response.json();
    // Store token in localStorage
    if (data.token) {
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  async loginWithGoogle(credential) {
    const response = await fetch(`${BASE_URL}/oauth/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ credential }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Google login failed');
    }

    const data = await response.json();
    // Store token in localStorage
    if (data.token) {
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  // Sign in with Apple — identityToken comes from the Capacitor plugin's
  // SignInWithApple.authorize() response. givenName/familyName are only
  // populated on the user's first sign-in (Apple does not return them on
  // subsequent calls), so we forward whatever the plugin gives us; the
  // server only persists them when creating a new user.
  async loginWithApple({ identityToken, givenName, familyName }) {
    const response = await fetch(`${BASE_URL}/oauth/apple`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identityToken, givenName, familyName }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Apple Sign-In failed');
    }
    const data = await response.json();
    if (data.token) {
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  async logout() {
    const response = await fetch(`${BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    
    // Clear local storage regardless of response
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    
    return response.ok;
  },

  async getCurrentUser() {
    const response = await fetch(`${BASE_URL}/auth/me`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Not authenticated');
    }
    
    return response.json();
  },

  getStoredUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated() {
    return !!localStorage.getItem('authToken');
  },

  // Puzzles
  async getPuzzles(difficulty = null, limit = 20, unsolvedOnly = false) {
    const params = new URLSearchParams();
    if (difficulty) params.append('difficulty', difficulty);
    if (unsolvedOnly) params.append('unsolved', '1');
    params.append('limit', limit);

    const response = await fetch(`${BASE_URL}/puzzles?${params}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch puzzles');
    }

    return response.json();
  },

  async getPuzzle(puzzleId) {
    const response = await fetch(`${BASE_URL}/puzzles/${puzzleId}`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch puzzle');
    }
    
    return response.json();
  },

  async submitPuzzleAttempt(puzzleId, completed, timeTaken, hintsUsed = 0) {
    const response = await fetch(`${BASE_URL}/puzzles/${puzzleId}/attempt`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ completed, timeTaken, hintsUsed }),
    });

    if (!response.ok) {
      throw new Error('Failed to submit attempt');
    }

    return response.json();
  },

  async getPuzzleStats() {
    const response = await fetch(`${BASE_URL}/puzzles/progress/stats`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch stats');
    }
    
    return response.json();
  },

  // Game management
  async createGame(gameMode = 'local', opponentName = null, aiConfig = null, timeControls = null, importedGame = null) {
    const response = await fetch(`${BASE_URL}/games`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        gameMode,
        opponentName,  // For local multiplayer or AI name
        aiConfig,
        timeControls,
        importedGame
      }),
    });

    checkAuthOk(response);
    if (!response.ok) {
      throw new Error('Failed to create game');
    }

    return response.json();
  },

  async getGame(gameId) {
    const response = await fetch(`${BASE_URL}/games/${gameId}`, {
      headers: getAuthHeaders(),
    });

    checkAuthOk(response);
    if (!response.ok) {
      throw new Error('Failed to fetch game');
    }

    return response.json();
  },

  async getRecentGames(limit = 10) {
    const response = await fetch(`${BASE_URL}/games?limit=${limit}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch games');
    }
    
    return response.json();
  },

  async updateGameStatus(gameId, status, winner = null) {
    const response = await fetch(`${BASE_URL}/games/${gameId}/status`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        status,
        winner
      }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to update game status');
    }
    
    return response.json();
  },

  // Move management
  async saveMove(gameId, moveData) {
    const response = await fetch(`${BASE_URL}/games/${gameId}/moves`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(moveData),
    });

    checkAuthOk(response);
    if (!response.ok) {
      // Get the actual error message from the server
      let errorMessage = 'Failed to save move';
      try {
        const errorData = await response.json();
        if (errorData.error || errorData.details) {
          errorMessage = errorData.error || errorData.details;
        }
      } catch (e) {
        // If we can't parse the error response, use default message
      }
      throw new Error(errorMessage);
    }
    
    return response.json();
  },

  async getGameMoves(gameId) {
    const response = await fetch(`${BASE_URL}/games/${gameId}/moves`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch moves');
    }
    
    return response.json();
  },

  // AI functionality
  async requestAIMove(gameId, board, playerColor, lastMove = null, options = {}) {
    const { timeoutMs = 300000, signal: externalSignal } = options;

    // Transient gateway errors (502 Bad Gateway, 503 Service Unavailable,
    // 504 Gateway Timeout) are usually the upstream restarting after an OOM
    // kill or a brief routing blip. One automatic retry with a short backoff
    // catches almost all of them without surfacing the error to the user.
    // 408 (server-side AI timeout) and 500 (engine crash) are NOT retried —
    // retrying those would just hit the same problem again.
    const RETRY_STATUSES = new Set([502, 503, 504]);
    const MAX_RETRIES = 1;
    const RETRY_BACKOFF_MS = 1500;

    const attempt = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const onExternalAbort = () => controller.abort();
      if (externalSignal) {
        if (externalSignal.aborted) controller.abort();
        else externalSignal.addEventListener('abort', onExternalAbort);
      }

      let response;
      try {
        response = await fetch(`${BASE_URL}/games/${gameId}/ai-move`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ board, playerColor, lastMove }),
          signal: controller.signal,
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          const aborted = externalSignal && externalSignal.aborted;
          const e = new Error(aborted ? 'AI request canceled' : `AI request timed out after ${Math.round(timeoutMs / 1000)}s`);
          e.kind = aborted ? 'aborted' : 'timeout';
          throw e;
        }
        const e = new Error(`Network error: ${err.message}`);
        e.kind = 'network';
        throw e;
      } finally {
        clearTimeout(timeoutId);
        if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);
      }

      checkAuthOk(response);
      if (!response.ok) {
        let body = null;
        try { body = await response.json(); } catch (_) { /* not JSON */ }
        const message = (body && (body.error || body.details)) || `AI request failed (HTTP ${response.status})`;
        const e = new Error(message);
        e.kind = body && body.timeout ? 'server-timeout' : (body && body.crashed ? 'engine-crash' : 'http-error');
        e.status = response.status;
        e.body = body;
        throw e;
      }

      return response.json();
    };

    let lastError;
    for (let i = 0; i <= MAX_RETRIES; i++) {
      try {
        return await attempt();
      } catch (e) {
        lastError = e;
        // Only retry on transient gateway errors. Server-timeout (408),
        // engine-crash (500), aborted, and network all bail immediately.
        if (i < MAX_RETRIES && e.kind === 'http-error' && RETRY_STATUSES.has(e.status)) {
          if (externalSignal && externalSignal.aborted) throw e;
          await new Promise(r => setTimeout(r, RETRY_BACKOFF_MS));
          continue;
        }
        throw e;
      }
    }
    throw lastError;
  },

  // Get current board state including frozen king zones
  async getBoardState(gameId) {
    const response = await fetch(`${BASE_URL}/games/${gameId}/board-state`, {
      headers: getAuthHeaders(),
    });

    checkAuthOk(response);
    if (!response.ok) {
      throw new Error('Failed to get board state');
    }

    return response.json();
  }
}; 